import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadSingleFile } from "../../services/FileDownloader";
import SyncedVideo from "./SyncedVideo";

const buttonStyle = {
  fontSize: 18,
  padding: "6px 12px",
  borderRadius: "8px",
  backgroundColor: "rgba(200, 200, 200, 0.25)",
  color: "white",
  border: "none",
  cursor: "pointer",
};

interface Props {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  videoUrl: string;
  currentTime: number;
  isPlaying: boolean;
  onLoaded?: (duration: number) => void;
  onTimeUpdate?: () => void;
  onPlayPauseChange?: (playing: boolean) => void;
  onEnded?: () => void;
}

const DraggableVideoWrapper: React.FC<Props> = ({ videoRef, videoUrl, currentTime, isPlaying, onLoaded, onTimeUpdate, onPlayPauseChange, onEnded }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [zoom, setZoom] = useState(.7);

  useEffect(() => {
    const handleMouseUp = () => setDragging(false);
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 0.7), 5));
  };

  const resetView = () => {
    setZoom(.6);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <Box
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onWheel={handleWheel}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        cursor: dragging ? "grabbing" : "grab",
      }}
    >
      <Box
        sx={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transition: dragging ? "none" : "transform 0.2s ease-out",
          maxWidth: "none",
          maxHeight: "none",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <SyncedVideo
          src={videoUrl}
          isPlaying={isPlaying}
          currentTime={currentTime}
          multiVid={false}
          onLoaded={(duration) => {
            if (onLoaded) {
              onLoaded(duration);
            }
          }}
          onPlayPauseChange={onPlayPauseChange}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          storeRef={(ref) => {
            if (videoRef) {
              videoRef.current = ref;
            }
          }}
          style={{
            // Override SyncedVideo's default sizing to allow for free zooming
            maxWidth: "none",
            maxHeight: "none",
            width: "auto",
            height: "auto",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          width: "100%",
          gap: 1,
          zIndex: 1,
        }}
      >
        <button onClick={() => setZoom((z) => Math.max(z - 0.1, 0.25))} style={buttonStyle}>-</button>
        <button onClick={() => downloadSingleFile(videoUrl)} style={buttonStyle}>
          <DownloadIcon />
        </button>
        <button onClick={resetView} style={buttonStyle}><RestartAltIcon /></button>
        <button onClick={() => setZoom((z) => Math.min(z + 0.1, 5))} style={buttonStyle}>+</button>
      </Box>
    </Box>
  );
};

export default DraggableVideoWrapper;
